import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { HardDrive, AlertTriangle, Eye, Sparkles, ArrowRight } from 'lucide-react';

export const OnboardingDialog = ({ open, onClose }) => {
    const [step, setStep] = useState(0);
    
    const steps = [
        {
            icon: HardDrive,
            iconBg: 'bg-blue-50',
            iconColor: 'text-blue-500',
            title: 'Your ideas live here',
            description: 'Everything is stored locally in your browser. Nothing leaves your device unless you export it.',
            warning: null,
        },
        {
            icon: AlertTriangle,
            iconBg: 'bg-amber-50',
            iconColor: 'text-amber-500',
            title: 'Understand the trade-off',
            description: 'Clearing browser data or switching browsers will delete your ideas permanently.',
            warning: 'No cloud backup. No recovery. Export regularly to keep your ideas safe.',
        },
        {
            icon: Eye,
            iconBg: 'bg-emerald-50',
            iconColor: 'text-emerald-500',
            title: 'Privacy by default',
            description: 'No accounts, no tracking, no analytics. Your thoughts remain yours alone.',
            warning: null,
        },
        {
            icon: Sparkles,
            iconBg: 'bg-indigo-50',
            iconColor: 'text-indigo-500',
            title: 'AI is your assistant',
            description: 'Optionally use AI to refine ideas. Bring your own API key or use our managed service.',
            warning: 'When using AI, your text is sent to the AI provider. Keep AI off for maximum privacy.',
        },
    ];
    
    const currentStep = steps[step];
    const isLastStep = step === steps.length - 1;
    const Icon = currentStep.icon;
    
    const handleNext = () => {
        if (isLastStep) {
            onClose();
        } else {
            setStep(s => s + 1);
        }
    };
    
    return (
        <Dialog open={open} onOpenChange={() => {}}>
            <DialogContent 
                className="sm:max-w-md rounded-2xl border-slate-200/60 p-0 overflow-hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <div className="p-8">
                    <DialogHeader className="space-y-4">
                        <div className={`mx-auto w-14 h-14 rounded-2xl ${currentStep.iconBg} flex items-center justify-center`}>
                            <Icon className={`w-7 h-7 ${currentStep.iconColor}`} />
                        </div>
                        <DialogTitle className="text-2xl font-light text-center text-slate-800">
                            {currentStep.title}
                        </DialogTitle>
                        <DialogDescription className="text-center text-base text-slate-500 leading-relaxed">
                            {currentStep.description}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {currentStep.warning && (
                        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <p className="text-sm text-amber-800 text-center">
                                {currentStep.warning}
                            </p>
                        </div>
                    )}
                </div>
                
                <DialogFooter className="p-6 pt-0 flex-col gap-4 sm:flex-col">
                    <Button
                        onClick={handleNext}
                        className="w-full rounded-full h-12 font-medium bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20"
                        data-testid="onboarding-next-btn"
                    >
                        {isLastStep ? (
                            <>
                                Start thinking
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                        ) : 'Continue'}
                    </Button>
                    
                    <div className="flex justify-center gap-2">
                        {steps.map((_, i) => (
                            <div
                                key={i}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    i === step ? 'bg-slate-800 w-6' : 'bg-slate-200'
                                }`}
                            />
                        ))}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
